import { useEffect, useRef, useState, lazy, Suspense } from "react";
import "@/App.css";
import {
  BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import api from "@/lib/api";
import { initNativeBridge } from "@/lib/native";
import Landing from "@/pages/Landing";

// Authenticated + secondary pages are code-split so the initial bundle stays small; each loads
// on demand when its route is first visited.
const InviteGate = lazy(() => import("@/pages/InviteGate"));
const AdminAccess = lazy(() => import("@/pages/AdminAccess"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const InboxPage = lazy(() => import("@/pages/Inbox"));
const SearchPage = lazy(() => import("@/pages/Search"));
const Receipts = lazy(() => import("@/pages/Receipts"));
const Intelligence = lazy(() => import("@/pages/Intelligence"));
const Signals = lazy(() => import("@/pages/Signals"));
const Content = lazy(() => import("@/pages/Content"));
const News = lazy(() => import("@/pages/News"));
const Projects = lazy(() => import("@/pages/Projects"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Review = lazy(() => import("@/pages/Review"));
const Ledger = lazy(() => import("@/pages/Ledger"));
const Scorecard = lazy(() => import("@/pages/Scorecard"));
const PublicScorecard = lazy(() => import("@/pages/PublicScorecard"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const Sentinel = lazy(() => import("@/pages/Sentinel"));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" data-testid="route-loading">
    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const match = hash.match(/session_id=([^&]+)/);
      if (!match) {
        navigate("/", { replace: true });
        return;
      }
      try {
        const res = await api.post("/auth/session", { session_id: match[1] });
        setUser(res.data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true, state: { user: res.data } });
      } catch (e) {
        console.error("Session exchange failed:", e);
        setError("Authentication failed. Please try signing in again.");
        setTimeout(() => navigate("/", { replace: true }), 2500);
      }
    };
    processSession();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">{error || "Signing you in..."}</p>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (user.access === "pending") return <Navigate to="/invite" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/invite" element={<InviteGate />} />
        <Route path="/s/:token" element={<PublicScorecard />} />
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/receipts" element={<Receipts />} />
          <Route path="/intelligence" element={<Intelligence />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/review" element={<Review />} />
          <Route path="/ledger" element={<Ledger />} />
          <Route path="/scorecard" element={<Scorecard />} />
          <Route path="/signals" element={<Signals />} />
          <Route path="/content" element={<Content />} />
          <Route path="/news" element={<News />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/admin/access" element={<AdminAccess />} />
          <Route path="/sentinel" element={<Sentinel />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Register the native (Capacitor) deep-link listener. No-op on web.
    initNativeBridge();
  }, []);
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" closeButton visibleToasts={3} />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
